import React, { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Pencil, Plus } from 'lucide-react'
import { useOutletContext } from 'react-router-dom'
import ApiService from '../../../../services/ApiService'
import { PurchaseOutletType } from '..'
import { PurchaseDemand, PurchaseDemandSourceDetail } from './types'
import CreateDemandModal from './CreateDemandModal'
import EditDemandModal from './EditDemandModal'
import IssueOrderModal from './IssueOrderModal'
import {
  ConsumerRow,
  ConsumersTable,
  EditButton,
  EmptyState,
  ExpandButton,
  Footer,
  FooterItem,
  ItemCode,
  ItemName,
  ItemRow,
  ItemType,
  NewDemandButton,
  NumericCol,
  ScrollArea,
  SupplierActionButton,
  SupplierActions,
  SupplierGroup,
  SupplierHeader,
  SupplierName,
  SupplierSummary,
  TopBar,
  Wrapper,
} from './style'

interface SupplierGroupData {
  key: string
  supplierCompanyId: number | null
  supplierName: string
  demands: PurchaseDemand[]
}

const formatQty = (value: string | number) => {
  const n = Number(value)
  return Number.isInteger(n) ? n.toString() : n.toFixed(2)
}

const PurchaseDemandTable = () => {
  const { refresh, setRefresh } = useOutletContext<PurchaseOutletType>()
  const [demands, setDemands] = useState<PurchaseDemand[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const [consumersByDemand, setConsumersByDemand] = useState<Record<number, PurchaseDemandSourceDetail[]>>({})
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingDemand, setEditingDemand] = useState<PurchaseDemand | null>(null)
  const [issueModal, setIssueModal] = useState<{
    mode: 'QUOTE' | 'ORDER'
    supplierCompanyId: number
    supplierName: string
    demands: PurchaseDemand[]
  } | null>(null)

  const loadDemands = async () => {
    setLoading(true)
    try {
      const res = await ApiService.purchaseDemand.findAll()
      setDemands(res.data?.data ?? res.data ?? [])
    } catch (err) {
      console.error('Erro ao carregar demandas de compra', err)
      setDemands([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDemands()
  }, [refresh])

  const groups = useMemo<SupplierGroupData[]>(() => {
    const map = new Map<string, SupplierGroupData>()
    for (const demand of demands) {
      const key = demand.SupplierCompany ? `s-${demand.SupplierCompany.id}` : 'no-supplier'
      if (!map.has(key)) {
        map.set(key, {
          key,
          supplierCompanyId: demand.SupplierCompany?.id ?? null,
          supplierName: demand.SupplierCompany?.companyName ?? 'Sem fornecedor definido',
          demands: [],
        })
      }
      map.get(key)!.demands.push(demand)
    }
    return Array.from(map.values()).sort((a, b) => a.supplierName.localeCompare(b.supplierName))
  }, [demands])

  const kpis = useMemo(() => {
    const totalDcs = demands.length
    const zeroStock = demands.filter((d) => Number(d.PurchaseMaterial.currentStock) <= 0).length
    const belowMin = demands.filter((d) => {
      const min = d.PurchaseMaterial.minStock
      return min != null && Number(d.PurchaseMaterial.currentStock) < Number(min)
    }).length
    const valueToBuy = demands.reduce(
      (sum, d) => sum + Number(d.quantityToBuy) * Number(d.PurchaseMaterial.unitCost ?? 0),
      0,
    )
    return { totalDcs, zeroStock, belowMin, valueToBuy }
  }, [demands])

  const toggleSelected = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleExpanded = async (demand: PurchaseDemand) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(demand.id)) next.delete(demand.id)
      else next.add(demand.id)
      return next
    })

    if (!consumersByDemand[demand.id]) {
      try {
        const res = await ApiService.purchaseDemand.getById(demand.id)
        const full = res.data?.data ?? res.data
        setConsumersByDemand((prev) => ({ ...prev, [demand.id]: full?.Sources ?? [] }))
      } catch (err) {
        console.error('Erro ao carregar consumidores da demanda', err)
        setConsumersByDemand((prev) => ({ ...prev, [demand.id]: [] }))
      }
    }
  }

  const openIssueModal = (mode: 'QUOTE' | 'ORDER', group: SupplierGroupData) => {
    const selectedInGroup = group.demands.filter((d) => selectedIds.has(d.id))
    if (!selectedInGroup.length || group.supplierCompanyId == null) return
    setIssueModal({
      mode,
      supplierCompanyId: group.supplierCompanyId,
      supplierName: group.supplierName,
      demands: selectedInGroup,
    })
  }

  const handleRefresh = () => {
    setSelectedIds(new Set())
    setRefresh((r) => !r)
  }

  return (
    <Wrapper>
      <TopBar>
        <NewDemandButton onClick={() => setShowCreateModal(true)}>
          <Plus size={14} /> Nova Demanda de Compra
        </NewDemandButton>
      </TopBar>

      <ScrollArea>
        {!loading && groups.length === 0 && (
          <EmptyState>Nenhuma demanda de compra em aberto.</EmptyState>
        )}

        {groups.map((group) => {
          const selectedInGroup = group.demands.filter((d) => selectedIds.has(d.id))
          const criticalInGroup = group.demands.filter(
            (d) => Number(d.PurchaseMaterial.currentStock) <= 0,
          ).length
          const valueInGroup = selectedInGroup.reduce(
            (sum, d) => sum + Number(d.quantityToBuy) * Number(d.PurchaseMaterial.unitCost ?? 0),
            0,
          )
          const canAct = selectedInGroup.length > 0 && group.supplierCompanyId != null

          return (
            <SupplierGroup key={group.key}>
              <SupplierHeader>
                <SupplierName>{group.supplierName}</SupplierName>
                <SupplierSummary>
                  <span>
                    <strong>{selectedInGroup.length}</strong>/{group.demands.length} selecionadas
                  </span>
                  {criticalInGroup > 0 && (
                    <span className='critical'>{criticalInGroup} críticas</span>
                  )}
                  <span>
                    Valor a comprar: <strong>R$ {valueInGroup.toFixed(2)}</strong>
                  </span>
                </SupplierSummary>
                <SupplierActions>
                  <SupplierActionButton disabled={!canAct} onClick={() => openIssueModal('QUOTE', group)}>
                    Solicitar cotação
                  </SupplierActionButton>
                  <SupplierActionButton disabled={!canAct} onClick={() => openIssueModal('ORDER', group)}>
                    Emitir pedido
                  </SupplierActionButton>
                </SupplierActions>
              </SupplierHeader>

              {group.demands.map((demand) => {
                const isExpanded = expandedIds.has(demand.id)
                const isCritical = Number(demand.PurchaseMaterial.currentStock) <= 0
                const unit = demand.PurchaseMaterial.UnitOfMeasurement?.name || ''
                const consumers = consumersByDemand[demand.id]

                return (
                  <React.Fragment key={demand.id}>
                    <ItemRow>
                      <input
                        type='checkbox'
                        checked={selectedIds.has(demand.id)}
                        onChange={() => toggleSelected(demand.id)}
                      />
                      <ExpandButton onClick={() => toggleExpanded(demand)}>
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </ExpandButton>
                      <ItemCode>{demand.code}</ItemCode>
                      <ItemName>
                        {demand.PurchaseMaterial.name}
                        <span>{demand.PurchaseMaterial.code || '-'}</span>
                      </ItemName>
                      <ItemType>
                        {demand.PurchaseMaterial.type === 'MATERIA_PRIMA' ? 'Matéria Prima' : 'Insumo'}
                      </ItemType>
                      <NumericCol critical={isCritical}>
                        {formatQty(demand.PurchaseMaterial.currentStock)} {unit}
                      </NumericCol>
                      <NumericCol>
                        {formatQty(demand.requiredQuantity)} {unit}
                      </NumericCol>
                      <NumericCol critical={Number(demand.quantityToBuy) > 0}>
                        {formatQty(demand.quantityToBuy)} {unit}
                      </NumericCol>
                      <EditButton onClick={() => setEditingDemand(demand)} title='Editar demanda'>
                        <Pencil size={14} />
                      </EditButton>
                    </ItemRow>

                    {isExpanded && (
                      <ConsumersTable>
                        {!consumers && 'Carregando...'}
                        {consumers && consumers.length === 0 && 'Sem produtos consumidores vinculados.'}
                        {consumers &&
                          consumers.map((source) => {
                            const pct =
                              Number(demand.requiredQuantity) > 0
                                ? (Number(source.totalConsumption) / Number(demand.requiredQuantity)) * 100
                                : 0
                            return (
                              <ConsumerRow key={source.id}>
                                <span>{source.Product?.referenceProduct || '-'}</span>
                                <span>{source.Product?.name || source.Services?.codeOfOP || '-'}</span>
                                <span>
                                  {source.piecesQuantity != null ? formatQty(source.piecesQuantity) : '-'} peças
                                </span>
                                <span>
                                  {source.consumptionPerPiece != null
                                    ? formatQty(source.consumptionPerPiece)
                                    : '-'}{' '}
                                  / peça
                                </span>
                                <span>
                                  {formatQty(source.totalConsumption)} {unit}
                                </span>
                                <span>{pct.toFixed(1)}%</span>
                              </ConsumerRow>
                            )
                          })}
                      </ConsumersTable>
                    )}
                  </React.Fragment>
                )
              })}
            </SupplierGroup>
          )
        })}
      </ScrollArea>

      <Footer>
        <FooterItem>
          <strong>{kpis.totalDcs}</strong>
          <span>Total de DCs</span>
        </FooterItem>
        <FooterItem>
          <strong>{kpis.zeroStock}</strong>
          <span>Estoque zerado</span>
        </FooterItem>
        <FooterItem>
          <strong>{kpis.belowMin}</strong>
          <span>Abaixo do mínimo</span>
        </FooterItem>
        <FooterItem>
          <strong>R$ {kpis.valueToBuy.toFixed(2)}</strong>
          <span>Valor a comprar</span>
        </FooterItem>
      </Footer>

      {showCreateModal && (
        <CreateDemandModal onClose={() => setShowCreateModal(false)} onCreated={handleRefresh} />
      )}

      {editingDemand && (
        <EditDemandModal
          demand={editingDemand}
          onClose={() => setEditingDemand(null)}
          onSaved={handleRefresh}
        />
      )}

      {issueModal && (
        <IssueOrderModal
          mode={issueModal.mode}
          supplierCompanyId={issueModal.supplierCompanyId}
          supplierName={issueModal.supplierName}
          demands={issueModal.demands}
          onClose={() => setIssueModal(null)}
          onSuccess={handleRefresh}
        />
      )}
    </Wrapper>
  )
}

export default PurchaseDemandTable
